import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import {
  buildAdmissionsTuitionContractViewModel,
  type AdmissionsTuitionContractSchoolInfo,
  type AdmissionsTuitionContractViewModel,
  type AdmissionsTuitionConfiguration,
} from '@triedge/students-families-domain';

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const MARGIN_X = 44;
const TOP_Y = 746;
const BOTTOM_Y = 40;
const BODY_WIDTH = PAGE_WIDTH - MARGIN_X * 2;
const DARK = rgb(0.08, 0.23, 0.24);
const TEXT = rgb(0.12, 0.18, 0.2);
const MUTED = rgb(0.42, 0.49, 0.5);
const BORDER = rgb(0.82, 0.87, 0.88);
const SOFT = rgb(0.94, 0.97, 0.97);

const normalizeText = (value: unknown): string => String(value ?? '').trim();

const decodeBase64 = (value: string): Uint8Array => {
  const runtime = globalThis as unknown as {
    atob?: (input: string) => string;
    Buffer?: { from: (input: string, encoding: 'base64') => Uint8Array };
  };
  if (typeof runtime.atob === 'function') {
    const binary = runtime.atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
  }
  if (runtime.Buffer?.from) {
    return Uint8Array.from(runtime.Buffer.from(value, 'base64'));
  }
  throw new Error('Base64 image decoding is unavailable in this runtime.');
};

const writeLine = (
  page: PDFPage,
  font: PDFFont,
  text: string,
  y: number,
  x = MARGIN_X,
  size = 11,
  color = TEXT,
) => {
  page.drawText(text, {
    x,
    y,
    size,
    font,
    color,
  });
};

const wrapText = (
  text: string,
  maxWidth: number,
  font: PDFFont,
  size: number,
): string[] => {
  const words = normalizeText(text).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [''];
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const proposal = line ? `${line} ${word}` : word;
    if (font.widthOfTextAtSize(proposal, size) <= maxWidth) {
      line = proposal;
      continue;
    }
    if (line) {
      lines.push(line);
      line = word;
      continue;
    }
    lines.push(word);
  }
  if (line) lines.push(line);
  return lines;
};

const fitFontSize = (
  text: string,
  font: PDFFont,
  maxWidth: number,
  preferredSize: number,
  minimumSize: number,
): number => {
  let size = preferredSize;
  while (size > minimumSize && font.widthOfTextAtSize(text, size) > maxWidth) {
    size -= 0.25;
  }
  return Math.max(size, minimumSize);
};

const truncateToWidth = (
  text: string,
  font: PDFFont,
  size: number,
  maxWidth: number,
): string => {
  if (font.widthOfTextAtSize(text, size) <= maxWidth) return text;
  const suffix = '...';
  let truncated = text;
  while (truncated.length > 0 && font.widthOfTextAtSize(`${truncated}${suffix}`, size) > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return truncated ? `${truncated.trimEnd()}${suffix}` : suffix;
};

const writeFittedLine = (
  page: PDFPage,
  font: PDFFont,
  text: string,
  y: number,
  x: number,
  maxWidth: number,
  preferredSize: number,
  color = TEXT,
  minimumSize = 6,
) => {
  const size = fitFontSize(text, font, maxWidth, preferredSize, minimumSize);
  writeLine(page, font, truncateToWidth(text, font, size, maxWidth), y, x, size, color);
};

export const buildAdmissionsTuitionContractPdfModel = (input: {
  configuration: AdmissionsTuitionConfiguration;
  parentSalutation: string;
  familyAddressLines: string[];
  schoolInfo?: AdmissionsTuitionContractSchoolInfo | null;
  generatedAt?: Date | string | null;
}): AdmissionsTuitionContractViewModel =>
  buildAdmissionsTuitionContractViewModel({
    configuration: input.configuration,
    parentSalutation: input.parentSalutation,
    familyAddressLines: input.familyAddressLines,
    schoolInfo: input.schoolInfo,
    generatedAt: input.generatedAt,
  });

export const buildAdmissionsTuitionContractPdfFromModel = async (
  model: AdmissionsTuitionContractViewModel,
): Promise<Uint8Array> => {
  const pdf = await PDFDocument.create();
  let page = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  const regular = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  let cursorY = TOP_Y;

  const addPage = () => {
    page = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
    cursorY = TOP_Y;
  };
  const ensureSpace = (height: number) => {
    if (cursorY - height < BOTTOM_Y) {
      addPage();
    }
  };
  const sectionHeader = (label: string) => {
    ensureSpace(25);
    page.drawRectangle({
      x: MARGIN_X,
      y: cursorY - 15,
      width: BODY_WIDTH,
      height: 19,
      color: DARK,
    });
    writeLine(page, bold, label.toUpperCase(), cursorY - 10, MARGIN_X + 8, 8, rgb(1, 1, 1));
    cursorY -= 25;
  };
  const drawWrapped = (
    text: string,
    x: number,
    maxWidth: number,
    size = 10,
    font = regular,
    color = TEXT,
  ) => {
    for (const line of wrapText(text, maxWidth, font, size)) {
      writeLine(page, font, line, cursorY, x, size, color);
      cursorY -= size + 4;
    }
  };
  const drawKeyValueBox = (
    label: string,
    lines: string[],
    x: number,
    width: number,
    height: number,
  ) => {
    const boxTop = cursorY;
    const contentMaxWidth = width - 20;
    page.drawRectangle({
      x,
      y: boxTop - height + 5,
      width,
      height,
      borderColor: BORDER,
      borderWidth: 1,
      color: rgb(0.99, 1, 1),
    });
    writeLine(page, bold, label.toUpperCase(), boxTop - 10, x + 10, 7, MUTED);
    let lineY = boxTop - 26;
    const bottomLimit = boxTop - height + 13;
    for (const [index, line] of lines.filter(Boolean).entries()) {
      const lineFont = index === 0 ? bold : regular;
      const size = index === 0 ? 9.5 : 7.5;
      for (const wrappedLine of wrapText(line, contentMaxWidth, lineFont, size)) {
        if (lineY < bottomLimit) return;
        writeFittedLine(page, lineFont, wrappedLine, lineY, x + 10, contentMaxWidth, size, TEXT, 5.5);
        lineY -= size + 3;
      }
    }
  };
  const tableRow = (
    values: string[],
    widths: number[],
    options: { header?: boolean; boldLast?: boolean } = {},
  ) => {
    const rowHeight = options.header ? 20 : 19;
    ensureSpace(rowHeight + 4);
    page.drawRectangle({
      x: MARGIN_X,
      y: cursorY - rowHeight + 5,
      width: BODY_WIDTH,
      height: rowHeight,
      color: options.header ? SOFT : rgb(1, 1, 1),
      borderColor: BORDER,
      borderWidth: 0.5,
    });
    let x = MARGIN_X + 8;
    values.forEach((value, index) => {
      const isLast = index === values.length - 1;
      const cellWidth = (widths[index] ?? 100) - 8;
      writeFittedLine(
        page,
        options.header || (options.boldLast && isLast) ? bold : regular,
        value,
        cursorY - 6,
        x,
        cellWidth,
        options.header ? 7 : 8.4,
        options.header ? MUTED : TEXT,
        options.header ? 5.5 : 6,
      );
      x += widths[index] ?? 100;
    });
    cursorY -= rowHeight;
  };

  const logoMatch = normalizeText(model.schoolLogo).match(/^data:image\/(png|jpe?g);base64,(.+)$/i);
  if (logoMatch?.[1] && logoMatch[2]) {
    try {
      const bytes = decodeBase64(logoMatch[2]);
      const image = logoMatch[1].toLowerCase().startsWith('png')
        ? await pdf.embedPng(bytes)
        : await pdf.embedJpg(bytes);
      const scale = Math.min(38 / image.width, 38 / image.height, 1);
      const dims = image.scale(scale);
      page.drawImage(image, {
        x: MARGIN_X,
        y: cursorY - 38,
        width: dims.width,
        height: dims.height,
      });
    } catch {
      page.drawRectangle({ x: MARGIN_X, y: cursorY - 40, width: 40, height: 40, borderColor: BORDER, borderWidth: 1 });
      writeLine(page, bold, model.schoolName.slice(0, 2).toUpperCase(), cursorY - 24, MARGIN_X + 11, 11, DARK);
    }
  } else {
    page.drawRectangle({ x: MARGIN_X, y: cursorY - 40, width: 40, height: 40, borderColor: BORDER, borderWidth: 1 });
    writeLine(page, bold, model.schoolName.slice(0, 2).toUpperCase(), cursorY - 24, MARGIN_X + 11, 11, DARK);
  }
  const dateX = PAGE_WIDTH - MARGIN_X - 92;
  const headerTextX = MARGIN_X + 52;
  const headerTextWidth = Math.max(220, dateX - headerTextX - 16);
  writeFittedLine(page, bold, model.schoolName, cursorY - 7, headerTextX, headerTextWidth, 15, DARK, 11);
  const contactLine = [model.schoolAddress, [model.schoolPhone, model.schoolEmail].filter(Boolean).join(' | ')]
    .filter(Boolean)
    .join('  ');
  writeFittedLine(page, regular, contactLine, cursorY - 22, headerTextX, headerTextWidth, 7.2, MUTED, 5.5);
  writeLine(page, bold, model.currentDateLabel.toUpperCase(), cursorY - 7, dateX, 7, MUTED);
  writeLine(page, bold, model.academicYear, cursorY - 18, dateX, 7, MUTED);
  page.drawLine({
    start: { x: MARGIN_X, y: cursorY - 50 },
    end: { x: PAGE_WIDTH - MARGIN_X, y: cursorY - 50 },
    thickness: 1.5,
    color: DARK,
  });

  cursorY -= 74;
  writeLine(page, bold, 'Tuition Contract', cursorY, MARGIN_X, 20, TEXT);
  cursorY -= 28;

  const boxHeight = 60;
  drawKeyValueBox(
    'Family',
    [model.parentSalutation || 'Family', ...model.familyAddressLines],
    MARGIN_X,
    (BODY_WIDTH - 14) / 2,
    boxHeight,
  );
  drawKeyValueBox(
    'Payment Plan',
    [
      model.paymentPlanLabel,
      model.installmentSummaryLabel,
    ],
    MARGIN_X + (BODY_WIDTH + 14) / 2,
    (BODY_WIDTH - 14) / 2,
    boxHeight,
  );
  cursorY -= boxHeight + 14;

  sectionHeader('Student Summary');
  tableRow(['Student', 'Grade', 'Tuition'], [245, 150, 90], { header: true });
  for (const child of model.studentRows) {
    tableRow(
      [child.name || 'Student', child.gradeLabel, child.tuitionLabel],
      [245, 150, 90],
      { boldLast: true },
    );
  }

  sectionHeader('Fees');
  if (model.feeRows.length === 0) {
    drawWrapped('No additional fees configured.', MARGIN_X + 8, BODY_WIDTH - 16, 9.5, regular, MUTED);
    cursorY -= 8;
  } else {
    tableRow(['Fee', 'Schedule', 'Amount'], [245, 170, 70], { header: true });
    for (const fee of model.feeRows) {
      tableRow(
        [
          fee.label || 'Configured fee',
          fee.scheduleLabel,
          fee.amountLabel,
        ],
        [245, 170, 70],
        { boldLast: true },
      );
    }
  }

  sectionHeader('Family Totals');
  const totals = [
    ['Tuition subtotal', model.tuitionSubtotalLabel],
    ['Multi-child discount', model.multiChildDiscountLabel],
    ['Staff/Chinuch discount', model.staffChinuchDiscountLabel],
    ['Total tuition', model.totalTuitionLabel],
    ['Total fees', model.totalFeesLabel],
    ['Financial aid credit', model.financialAidCreditLabel],
    ['Fundraising obligation', model.fundraisingObligationLabel],
    ['Total obligation', model.totalObligationLabel],
  ];
  for (const [label, value] of totals) {
    tableRow([label, value], [365, 120], { boldLast: true });
  }

  ensureSpace(108);
  cursorY -= 36;
  const signatureWidth = (BODY_WIDTH - 22) / 2;
  const rightX = MARGIN_X + signatureWidth + 22;
  page.drawLine({ start: { x: MARGIN_X, y: cursorY }, end: { x: MARGIN_X + signatureWidth, y: cursorY }, thickness: 0.8, color: TEXT });
  page.drawLine({ start: { x: rightX, y: cursorY }, end: { x: rightX + signatureWidth, y: cursorY }, thickness: 0.8, color: TEXT });
  writeLine(page, bold, 'Parent/Guardian Signature', cursorY - 13, MARGIN_X, 7.2, MUTED);
  writeLine(page, bold, 'School administrator signature', cursorY - 13, rightX, 7.2, MUTED);
  cursorY -= 36;
  page.drawLine({ start: { x: MARGIN_X, y: cursorY }, end: { x: MARGIN_X + signatureWidth, y: cursorY }, thickness: 0.8, color: TEXT });
  page.drawLine({ start: { x: rightX, y: cursorY }, end: { x: rightX + signatureWidth, y: cursorY }, thickness: 0.8, color: TEXT });
  writeLine(page, bold, 'Printed name', cursorY - 13, MARGIN_X, 7.2, MUTED);
  writeLine(page, bold, 'Printed name', cursorY - 13, rightX, 7.2, MUTED);

  const footer = [model.schoolAddress, [model.schoolPhone, model.schoolEmail].filter(Boolean).join(' | ')].filter(Boolean).join(' | ');
  if (footer) {
    page.drawLine({ start: { x: MARGIN_X, y: BOTTOM_Y + 14 }, end: { x: PAGE_WIDTH - MARGIN_X, y: BOTTOM_Y + 14 }, thickness: 0.5, color: BORDER });
    writeFittedLine(page, regular, footer, BOTTOM_Y, MARGIN_X + 50, BODY_WIDTH - 100, 6.2, MUTED, 5);
  }

  return pdf.save();
};

export const buildAdmissionsTuitionContractPdf = async (input: {
  configuration: AdmissionsTuitionConfiguration;
  parentSalutation: string;
  familyAddressLines: string[];
  schoolInfo?: AdmissionsTuitionContractSchoolInfo | null;
  generatedAt?: Date | string | null;
}): Promise<Uint8Array> =>
  buildAdmissionsTuitionContractPdfFromModel(buildAdmissionsTuitionContractPdfModel(input));
